<template>
  <div>
    <section class="section">
      <vue-markdown
        class="desc"
        :source="point.desc || ''"
        :anchor-attributes="{ target: '_blank', rel: 'nofollow' }"
      ></vue-markdown>
      <div v-if="point.transferEnable" class="transfer" @click="toggleTransferPoint">
        <div class="transfer-btn">{{ $t('points.transfer') }}</div>
      </div>
    </section>
    <div
      class="section-title"
      v-if="point.expire.point !== 0"
    >
      {{ $t("points.limited") }}
    </div>
    <section
      class="section section-limited"
      v-if="point.expire.point !== 0"
    >
      <div class="date">
        {{ $t("points.expire") }} {{ point.expire.date }}
      </div>
      <div class="points">
        <span class="amount">{{ point.expire.point | properNumber }}</span>
        {{ point.type === 'DEPOSIT' ? $tc("points.dollars", point.expire.point) : $tc("points.points", point.expire.point) }}
      </div>
    </section>

    <template v-if="point.type === 'POINT' || point.type === 'DEPOSIT'">
      <div class="section-title" v-if="!brand.subBrand">
        {{ $t("points.rewards") }}
      </div>
      <div class="section-title-container"
        v-on:click="subBrandFilter(brand.subBrand)"
        v-else
      >
        <div class="section-title">{{ $t("points.rewards") }}</div>
        <div class="subbrand-dropdown">
          <span v-if="subBrand.label">{{ subBrand.label }}</span>
          <span v-else>{{ $t("common.allSubBrand") }}</span>
          <i class="material-icons">expand_more</i>
        </div>
      </div>
      <section class="section section--rewards">
        <div class="rewards">
          <div
            class="item"
            :class="{ 'is-disabled': reward.redeem_type === '0' }"
            v-for="reward in rewards"
            :key="reward.token"
            v-on:click="selectReward(reward)"
          >
            <div
              class="item-background"
              v-lazy:background-image="reward.image"
            ></div>
            <div class="item-left">
              <div class="name">{{ reward.title }}</div>
              <div class="meta" v-if="reward.redeem_type === '0'">{{ $t("points.disabled") }}</div>
            </div>
            <div class="item-right">
              <div class="cost">{{ reward.require_point | properNumber }}</div>
              <div class="unit">
                {{ point.type === 'DEPOSIT' ? $tc("points.dollars", reward.require_point) : $tc("points.points", reward.require_point) }}
              </div>
            </div>
          </div>
        </div>
      </section>
    </template>

    <template v-if="point.type === 'MILESTONE'">
      <svg class="svg-def" width="48" height="48" xmlns="http://www.w3.org/2000/svg">
        <symbol id="hex">
          <path d="M29 2.887l10.785 6.226a10 10 0 015 8.66v12.453a10 10 0 01-5 8.66L29 45.114a10 10 0 01-10 0L8.215 38.887a10 10 0 01-5-8.66V17.773a10 10 0 015-8.66L19 2.886a10 10 0 0110 0z" :stroke="`#${point.color}`" stroke-width="1" fill="none" fill-rule="evenodd" stroke-opacity=".25"/>
        </symbol>
        <symbol id="unlock-hex">
          <path d="M29 2.887l10.785 6.226a10 10 0 015 8.66v12.453a10 10 0 01-5 8.66L29 45.114a10 10 0 01-10 0L8.215 38.887a10 10 0 01-5-8.66V17.773a10 10 0 015-8.66L19 2.886a10 10 0 0110 0z" :fill="`#${point.color}`" fill-rule="evenodd"/>
        </symbol>
      </svg>

      <div class="section-title">{{ $t("points.progress") }}</div>
      <section class="section section--milestones" :style="`background-color: #${point.bgColor}; color: #${point.color}`">
        <div class="stat">
          <div v-on:click="backdoor = 1">{{ $t("points.archive") }}</div>
          <div class="archive"><span class="amount">{{ Math.round(point.point / point.goal * 100) }}</span>%</div>
        </div>
        <div class="stones">
          <div
            class="stone"
            v-for="(badge, index) in point.badges"
            v-bind:key="`badge#${index}`"
            v-on:click="selecteBadge(index)"
          >
            <div class="container container--unlock" v-if="badge.status !== 0">
              <div class="item">
                <svg class="hex" viewBox="0 0 48 48">
                  <use xlink:href="#unlock-hex"/>
                </svg>
                <div class="content">
                  <span class="label">{{ badge.name }}</span>
                </div>
                <div class="unlock"
                  v-if="badge.has_reward"
                >
                  <i class="material-icons">lock_open</i>
                </div>
              </div>
            </div>
            <div class="container" v-else>
              <div class="item">
                <svg class="hex" viewBox="0 0 48 48">
                  <use xlink:href="#hex"/>
                </svg>
                <div class="content">
                  <span class="label">{{ badge.name }}</span>
                </div>
                <div class="lock"
                  v-if="badge.has_reward"
                  :style="`background-color: #${point.color}`"
                >
                  <i class="material-icons">lock</i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div class="section-title">{{ $t("points.rewards") }}</div>
      <section class="section section--milestone-rewards">
        <div class="rewards">
          <div
            class="item"
            v-for="reward in rewards"
            :key="reward.token"
            :style="{ 'background-color': reward.status === 2 ? `#${point.bgColor}` : null }"
            v-on:click="selectMilestoneReward(reward)"
          >
            <div class="item-fig">
              <div class="image" :style="'background-image: url(' + reward.image + ')'" />
              <div
                class="unlock"
                v-if="reward.status === 2 || reward.status === 3"
                :style="{ 'color': `#${point.color}` }"
              >
                <i class="material-icons">lock_open</i>
              </div>
              <div
                class="lock"
                v-else-if="reward.status === 0"
                :style="`background-color: #${point.color}`"
              >
                <i class="material-icons">lock</i>
              </div>
            </div>
            <div class="item-name">
              {{ reward.title }}
            </div>
            <div
              class="item-info"
              v-if="'remain_count' in reward"
            >
              {{ $t("points.remain") }} {{ reward.remain_count.toLocaleString() }}
            </div>
            <div class="item-tool">
              <a
                v-if="reward.status === 2" :style="`color: #${point.color}`"
                v-on:click.stop="claimReward(reward.token)"
              >
                {{ $t("points.claim") }}
              </a>
              <span v-else-if="reward.status === 3">
                {{ $t("points.received") }}
              </span>
              <span v-else-if="reward.status === 0">
                {{ $tc("points.toUnlock", reward.require_point, { point: reward.require_point }) }}
              </span>
            </div>
          </div>
        </div>
      </section>
    </template>

    <div class="section-title">{{ $t("points.history") }}</div>
    <section class="section section--history">
      <div class="history">
        <div
          class="item"
          v-for="history in point.history.slice(0, historyLength)"
          :key="history.id"
        >
          <div
            class="history-row"
            :class="{ 'has-more': history.more }"
            v-on:click="selectHistory(history)"
          >
            <div class="left">
              <div class="content">{{ history.content }}</div>
              <div class="time">
                {{ history.create_time | moment("calendar") }}
              </div>
              <div class="expire" v-if="history.expire_time">
                {{ $t("points.pointExpire") }} {{ history.expire_time | moment("calendar") }}
              </div>
            </div>
            <div class="right">
              <div
                class="detail-toggle"
                :class="{ 'is-open': historyDetails[history.id] }"
                v-if="history.more"
              >
                <div class="toggle-loader" v-if="historyDetails[history.id] === null"><span></span><span></span><span></span></div>
                <i class="material-icons" v-else>expand_more</i>
              </div>
            </div>
          </div>
          <transition name="fade">
            <div class="details" v-if="historyDetails[history.id]">
              <div
                class="detail"
                v-for="(key, index) in historyDetails[history.id]"
                :key="historyDetails[history.id] + '#' + index"
              >
                <div class="name">{{ key.key }}</div>
                <div class="value" v-html="key.value"></div>
              </div>
            </div>
          </transition>
        </div>
        <div class="item" v-if="point.history.length === 0">
          <div class="content">{{ $t("points.historyEmpty") }}</div>
        </div>
        <a
          class="more"
          v-if="historyLength < point.history.length"
          v-on:click="loadMoreHistory"
        >
          {{ $t("common.more") }}
        </a>
      </div>
    </section>

    <picker
      :visable="picker"
      :type="pickerType"
      :options="pickerOptions"
      v-on:pickOption="pickOption"
      v-on:reset="resetOption"
      v-on:togglePicker="picker = !picker"
    ></picker>

    <div class="qrcode-reader" v-if="showQrcodeReader">
      <svg class="qrcode-box" width="674px" height="674px" viewBox="0 0 674 674" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
          <title>Shape</title>
          <g id="Page-1" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd" stroke-linecap="round">
              <path d="M7,181.344194 C7,120.720919 13.3121416,98.7374551 25.1650239,76.5744922 C37.0179062,54.4115292 54.4115292,37.0179062 76.5744922,25.1650239 C98.7374551,13.3121416 120.720919,7 181.344194,7 M492.655806,7 C553.279081,7 575.262545,13.3121416 597.425508,25.1650239 C619.588471,37.0179062 636.982094,54.4115292 648.834976,76.5744922 C660.687858,98.7374551 667,120.720919 667,181.344194 M667,492.655806 C667,553.279081 660.687858,575.262545 648.834976,597.425508 C636.982094,619.588471 619.588471,636.982094 597.425508,648.834976 C575.262545,660.687858 553.279081,667 492.655806,667 M181.344194,667 C120.720919,667 98.7374551,660.687858 76.5744922,648.834976 C54.4115292,636.982094 37.0179062,619.588471 25.1650239,597.425508 C13.3121416,575.262545 7,553.279081 7,492.655806" id="Shape" stroke="#FFFFFF" stroke-width="14"></path>
          </g>
      </svg>
      <button class="qrcode-reader-close" v-on:click="showQrcodeReader = false">{{ $t('common.close') }}</button>
      <qrcode-stream v-on:decode="onDecodePointsQrcode" v-on:init="qrcodeReaderInit" />
    </div>

    <milestone-reward-detail
      :visable="milestoneRewardDetailDisplay"
      :reward="milestoneReward"
      :point="point"
      v-on:close="closeMilestoneReward()"
      v-on:claim="claimReward(milestoneReward.token)"
    />

    <point-transfer-modal
      :is-visible="pointTransfer"
      :max-amount="Number(point.point)"
      :default-country="'TW'"
      @cancel="handleCancelTransfer"
      @submit="handleTransferSubmit"
    />
  </div>
</template>

<script>
import { mapState, mapMutations, mapActions } from 'vuex';
import VueMarkdown from 'vue-markdown';
import swal from 'sweetalert2';
import { QrcodeStream } from 'vue-qrcode-reader';

import Axios from '@/helpers/axios';

import Picker from '@/components/Picker.vue';
import MilestoneRewardDetail from '@/components/MilestoneRewardDetail.vue';
import pointTransferModal from '@/components/PointTransferModal.vue';

export default {
  name: 'PointItem',
  computed: {
    ...mapState([
      'brand',
      'point',
      'client',
    ]),
    rewards() {
      const allRewards = this.point.rewards;
      if (this.subBrand.label) {
        return allRewards.filter((element) => {
          if (element.available_store === null) {
            return element;
          }
          const availableStores = element.available_store.split(',').map(Number);
          const intersection = availableStores.filter(store => this.subBrand.value.includes(store));
          if (intersection.length === 0) {
            return false;
          }
          return element;
        });
      }
      return allRewards;
    },
  },
  data() {
    return {
      historyLength: 5,
      subBrand: {
        label: null,
        value: null,
      },
      picker: false,
      pickerType: null,
      pickerOptions: [],
      backdoor: 0,
      spend: null,
      points: null,
      historyDetails: {},

      showQrcodeReader: false,

      milestoneReward: {},
      milestoneRewardDetailDisplay: false,
      pointTransfer: false,
    };
  },
  methods: {
    ...mapMutations(['setLoader', 'setReward', 'setRewardUltrasound']),
    ...mapActions(['getPoint']),
    pickOption(option) {
      if (this.pickerType === 'subBrand') {
        this.subBrand = option;
      }
      this.picker = false;
      this.pickerType = null;
    },
    resetOption() {
      if (this.pickerType === 'subBrand') {
        this.subBrand = {
          label: null,
          value: null,
        };
      }
      this.picker = false;
      this.pickerType = null;
    },
    subBrandFilter(subBrand) {
      if (!subBrand) {
        return;
      }

      const options = [{
        label: this.$i18n.t('common.allSubBrand'),
        value: null,
        reset: true,
      }];

      subBrand.forEach((element) => {
        options.push({
          label: element.name,
          // eslint-disable-next-line no-underscore-dangle
          value: element._stores,
        });
      });

      this.pickerType = 'subBrand';
      this.pickerOptions = options;
      this.picker = true;
    },
    selectReward(reward, redirect = false) {
      this.setReward(reward);
      const token = reward.token;
      if (redirect) {
        this.setLoader(true);
        this.$router.replace({ name: 'Reward', params: { pointToken: this.point.token, token } });
      } else {
        this.setLoader(true);
        this.$router.push({ name: 'Reward', params: { pointToken: this.point.token, token } });
      }
    },
    selectMilestoneReward(reward) {
      this.milestoneReward = reward;
      this.milestoneRewardDetailDisplay = true;
    },
    closeMilestoneReward() {
      this.milestoneReward = {};
      this.milestoneRewardDetailDisplay = false;
    },
    selecteBadge(index) {
      if (index + 1 === this.point.badges.length && this.backdoor === 1) {
        this.storePIN()
          .then((storePIN) => {
            if (storePIN.value !== undefined && !storePIN.isDenied) {
              this
                .spendOrPoints(this.point.mode[0])
                .then(() => {
                  if (this.point.mode[1]) {
                    this
                      .spendOrPoints(this.point.mode[1])
                      .then(() => {
                        this.givePoints(storePIN.value);
                      });
                  } else {
                    this.givePoints(storePIN.value);
                  }
                });
            }

            if (storePIN.isDenied) {
              this.showQrcodeReader = true;
            }
          });
      } else {
        this.backdoor = 0;
      }
    },
    claimReward(token) {
      const data = {
        ...this.$route.query,
        lang: this.$i18n.locale,
        p: this.client.phoneType,
        brand_id: this.$route.params.brand,
        dtk: this.client.dtk,
        _mission_badge: token,
      };

      this.setLoader(true);

      Axios({
        method: 'post',
        url: '/mission/claimReward',
        data,
      })
        .then((response) => {
          if (response.data.code === 500) {
            swal.fire({
              text: response.data.msg,
              confirmButtonText: this.$i18n.t('common.ok'),
            })
              .then(() => {
                this.getPoint(this.$route.params.token);

                this.$nextTick(() => {
                  this.setLoader(false);
                });
              });
          } else if (response.data.code === 495) {
            swal.fire({
              text: response.data.msg,
              confirmButtonText: this.$i18n.t('common.back'),
            })
              .then(() => {
                this.setLoader(false);
              });
          }
        });
    },
    storePIN() {
      return swal.fire({
        text: this.$i18n.t('reward.enterStorePIN'),
        confirmButtonText: this.$i18n.t('common.ok'),
        showCancelButton: true,
        cancelButtonText: this.$i18n.t('common.cancel'),
        showDenyButton: true,
        denyButtonText: this.$i18n.t('common.scan'),
        reverseButtons: true,
        input: 'password',
        inputAttributes: {
          autocomplete: 'off',
          autocapitalize: 'off',
          autocorrect: 'off',
          spellcheck: 'false',
        },
      });
    },
    spendOrPoints(type) {
      if (type === 'SPEND') {
        return this.enterSpend();
      } else if (type === 'POINT') {
        return this.enterPoints();
      }
      return Promise().reject();
    },
    async enterSpend() {
      let swalValue;

      await swal.fire({
        text: this.$i18n.t('reward.enterSpend'),
        confirmButtonText: this.$i18n.t('common.ok'),
        showCancelButton: true,
        cancelButtonText: this.$i18n.t('common.cancel'),
        reverseButtons: true,
        input: 'text',
        inputAttributes: {
          autocomplete: 'off',
          autocapitalize: 'off',
          autocorrect: 'off',
          spellcheck: 'false',
          pattern: '[0-9]*',
          inputmode: 'numeric',
        },
      })
        .then((spend) => {
          swalValue = spend;
        });

      if (swalValue.value !== undefined) {
        this.spend = Number(swalValue.value);
        return Promise.resolve(swalValue);
      }

      return Promise.reject();
    },
    async enterPoints() {
      let swalValue;

      await swal.fire({
        text: this.$i18n.t('reward.enterPoints'),
        confirmButtonText: this.$i18n.t('common.ok'),
        showCancelButton: true,
        cancelButtonText: this.$i18n.t('common.cancel'),
        reverseButtons: true,
        input: 'text',
        inputAttributes: {
          autocomplete: 'off',
          autocapitalize: 'off',
          autocorrect: 'off',
          spellcheck: 'false',
          pattern: '[0-9]*',
          inputmode: 'numeric',
        },
      })
        .then((points) => {
          swalValue = points;
        });

      if (swalValue.value !== undefined) {
        this.points = Number(swalValue.value);
        return Promise.resolve(swalValue);
      }

      return Promise.reject();
    },
    givePoints(code) {
      const data = {
        ...this.$route.query,
        lang: this.$i18n.locale,
        p: this.client.phoneType,
        brand_id: this.$route.params.brand,
        dtk: this.client.dtk,
        _point_activity: this.point.token,
        code,
      };

      if (this.spend) data.spend = this.spend;
      if (this.points) data.point = this.points;

      this.setLoader(true);

      Axios({
        method: 'post',
        url: '/mission/givePoint',
        data,
      })
        .then((response) => {
          if (response.data.code === 500) {
            swal.fire({
              text: response.data.msg,
              confirmButtonText: this.$i18n.t('common.ok'),
            })
              .then(() => {
                this.getPoint(this.$route.params.token);

                this.$nextTick(() => {
                  this.setLoader(false);
                });
              });
          } else if (response.data.code === 495) {
            swal.fire({
              text: response.data.msg,
              confirmButtonText: this.$i18n.t('common.back'),
            })
              .then(() => {
                this.setLoader(false);
              });
          }
        });
    },
    selectHistory(history) {
      if (history.more === false) return;

      if (this.historyDetails[history.id]) {
        this.$delete(this.historyDetails, history.id);

        return;
      }

      this.$set(this.historyDetails, history.id, null);

      const data = {
        ...this.$route.query,
        lang: this.$i18n.locale,
        p: this.client.phoneType,
        brand_id: this.$route.params.brand,
        dtk: this.client.dtk,
        id: history.id,
      };

      Axios({
        method: 'post',
        url: '/v2/ocard/getTransDetail',
        data,
      })
        .then((response) => {
          if (response.data.code === 500) {
            this.$set(this.historyDetails, history.id, response.data.data);
          }
        });
    },
    loadMoreHistory() {
      this.historyLength += 5;
    },
    async qrcodeReaderInit(promise) {
      try {
        await promise;
      } catch (error) {
        let errorMsg;

        if (error.name === 'NotAllowedError') {
          errorMsg = this.$i18n.t('common.notAllowedCameraError');
        } else if (error.name === 'NotFoundError') {
          errorMsg = this.$i18n.t('common.notFoundCameraError');
        } else if (error.name === 'NotSupportedError') {
          errorMsg = this.$i18n.t('common.notSupportedNetworkError');
        } else if (error.name === 'NotReadableError') {
          errorMsg = this.$i18n.t('common.notReadableCameraError');
        } else if (error.name === 'StreamApiNotSupportedError') {
          errorMsg = this.$i18n.t('common.streamApiNotSupportedError');
        } else if (error.name === 'InsecureContextError') {
          errorMsg = this.$i18n.t('common.notSupportedNetworkError');
        } else {
          errorMsg = this.$i18n.t('common.unableAccessCameraError');
        }

        this.showQrcodeReader = false;

        let errorText = '';
        if (this.$i18n.locale === 'zh-Hant') {
          errorText = `${errorMsg}，${this.$i18n.t('common.unableScanError')}`;
        } else if (this.$i18n.locale === 'en' || this.$i18n.locale === 'vi' || this.$i18n.locale === 'th') {
          errorText = `${errorMsg} ${this.$i18n.t('common.unableScanError')}`;
        } else {
          errorText = errorMsg;
        }


        swal.fire({
          text: errorText,
        });
      }
    },
    onDecodePointsQrcode(code) {
      this.showQrcodeReader = false;

      this.spendOrPoints(this.point.mode[0])
        .then(() => {
          if (this.point.mode[1]) {
            this
              .spendOrPoints(this.point.mode[1])
              .then(() => {
                this.givePoints(code);
              });
          } else {
            this.givePoints(code);
          }
        });
    },
    toggleTransferPoint() {
      swal.fire({
        text: this.$i18n.t('points.transferReminderDescription'),
        confirmButtonText: this.$i18n.t('common.next'),
        showCancelButton: true,
        cancelButtonText: this.$i18n.t('common.cancel'),
        reverseButtons: true,
      }).then((send) => {
        if (send.value) {
          this.pointTransfer = true;
        }
      });
    },
    handleCancelTransfer() {
      this.pointTransfer = false;
    },
    async handleTransferSubmit(shareData, callback) {
      const data = {
        ...this.$route.query,
        lang: this.$i18n.locale,
        p: this.client.phoneType,
        dtk: this.client.dtk,
        _point_activity: this.point.token,
        ...shareData,
      };

      try {
        const response = await Axios({
          method: 'post',
          url: '/v2/Ocard/pointTransfer',
          data,
        });

        const { code, msg } = response.data;

        if (code === 500) {
          swal.fire({
            text: this.$i18n.t('points.transferSuccessfulDescription'),
            confirmButtonText: this.$i18n.t('common.ok'),
          }).then(() => {
            callback(true);
            window.location.reload();
          });
        } else {
          swal.fire({
            text: msg,
            confirmButtonText: this.$i18n.t('common.ok'),
          }).then(() => {
            callback(true);
            this.pointTransfer = false;
          });
        }
      } catch (e) {
        this.phoneError = '';
        this.member = '';
        callback(false);
      }
    },
  },
  components: {
    Picker,
    VueMarkdown,
    QrcodeStream,
    MilestoneRewardDetail,
    pointTransferModal,
  },
};
</script>

<style lang="sass" scoped>
$cb: cubic-bezier(.4, 0, .2, 1)

@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@900&family=Noto+Sans+TC:wght@900&display=swap')

.transfer
  text-align: center
  &-btn
    display: inline-block
    padding: 8px 16px

    border-radius: 6px

    background-color: #f5f5f5
    cursor: pointer

    color: #8E8E8E
    font-size: 12px
    font-weight: 500

.section-title
  margin: 24px 24px 8px

  color: #686868
  font-size: 0.875rem
  font-weight: bold
  letter-spacing: 2px
  text-transform: uppercase

  & + .section
    margin-top: 0

  &-container
    display: flex
    align-items: center
    margin: 24px 24px 8px

    & + .section
      margin-top: 0

    .section-title
      flex-grow: 1
      margin: 0

.subbrand-dropdown
  padding: 4px 8px

  border-radius: 10px

  background-color: #F5F5F5

  font-size: 0.875rem
  font-weight: bold
  color: #8E8E8E

  cursor: pointer

  span
    vertical-align: middle

  .material-icons
    margin-left: 8px

    font-size: 0.75rem
    vertical-align: middle

.empty
  padding: 15% 0

  text-align: center
  font-weight: bold
  color: #CFD8DC

  .material-icons
    display: block
    margin-bottom: 12px

    font-size: 5.5rem

.desc
  margin: 24px 16px 0

  font-size: 0.875rem
  color: #8E8E8E

.section-limited
  display: flex
  padding: 0 16px
  justify-content: space-between

  .date
    line-height: 36px
    font-size: 0.875rem
    color: #8E8E8E

  .points
    font-size: 0.75rem
    font-weight: bold

  .amount
    margin-right: 2px
    display: inline-block

    font-size: 2.25rem
    font-weight: 300
    line-height: 36px

.section--rewards
  .rewards
    padding: 0 16px
    display: flex
    flex-direction: column

    .item
      padding: 13px 15px
      margin-bottom: 12px
      display: flex
      box-shadow: 0 1px 3px 0 rgba(0, 0, 0, .15)
      align-items: center
      position: relative
      overflow: hidden

      border-radius: 16px

      color: white
      font-weight: bold

      background-image: linear-gradient(-180deg, rgba(51, 51, 51, .15) 0%, rgba(51, 51, 51, .75) 100%)
      background-size: cover
      background-position: center

      cursor: pointer

      &:hover
        .item-background
          opacity: .75

      &.is-disabled
        order: 1

        background-image: linear-gradient(-180deg, rgba(51, 51, 51, .15) 0%, rgba(51, 51, 51, .75) 100%)

        .name
          font-size: 0.875rem

        .item-background
          opacity: .25

      &-background
        position: absolute
        top: 0
        right: 0
        bottom: 0
        left: 0
        z-index: -1

        background-size: cover
        background-position: center

      &-left
        flex-grow: 1

      &-right
        text-align: right
        white-space: nowrap

    .name
      flex-grow: 1
      margin-right: 24px

      font-weight: bold

    .meta
      font-size: 0.75rem
      color: rgba(255, 255, 255, .75)
      font-weight: normal

    .cost,
    .unit
      display: inline-block

    .cost
      font-weight: 300
      font-size: 2.25rem

    .unit
      margin-left: 4px

      font-size: 0.75rem
      font-weight: bold

.svg-def
  display: none

.section--milestones
  margin: 0 16px
  padding: 24px 24px 16px 24px

  border-radius: 16px

  .stat
    display: flex
    justify-items: center
    justify-content: space-between
    align-items: center

    font-size: 0.875rem

    .archive
      font-size: 0.75rem
      font-weight: bold

    .amount
      margin-right: 2px
      display: inline-block

      font-size: 2.25rem
      font-weight: 300
      line-height: 36px

  .stones
    margin-top: 16px
    display: flex
    flex-wrap: wrap
    justify-content: space-between

  .stone
    width: calc((100% - 32px) / 5)
    margin-bottom: 8px

    .container
      position: relative
      padding-top: 100%

      &--unlock
        .content
          color: white

        .hex
          animation-duration: .5s
          animation-name: bounceIn
          animation-timing-function: ease-in-out

    .item
      position: absolute
      top: 0
      right: 0
      bottom: 0
      left: 0
      width: 100%
      height: 100%

      text-align: center

    .hex
      position: absolute
      top: 0
      right: 0
      bottom: 0
      left: 0
      width: 100%
      height: 100%
      z-index: 100

    .content
      position: absolute
      top: 0
      right: 0
      bottom: 0
      left: 0
      width: 100%
      height: 100%
      z-index: 110
      display: flex
      justify-conetnet: center
      align-items: center

      text-align: center

    .label
      flex-grow: 1

      font-size: 1.2081rem
      font-weight: 900
      line-height: 1

      font-family: 'Montserrat', 'Noto Sans TC', sans-serif

      @media only screen and (min-width : 992px)
        font-size: 2.6669rem

    .lock,
    .unlock
      position: absolute
      top: 0
      right: 0
      bottom: 0
      left: auto
      width: 20px
      height: 20px
      z-index: 120
      display: flex
      justify-conetnet: center
      align-items: center

      border-radius: 100%

      text-align: center

      .material-icons
        font-size: 0.75rem
        flex-grow: 1

    .lock
      color: white

    .unlock
      background-color: white

.section--milestone-rewards
  margin: 0 16px

  .rewards
    display: flex
    flex-wrap: wrap
    justify-items: center
    justify-content: space-between

  .item
    width: calc((100% - 8px) / 2)
    padding: 24px
    margin-bottom: 8px
    display: flex
    flex-direction: column

    border-radius: 16px

    background-color: #F5F5F5

    text-align: center

  .item-fig
    position: relative
    padding: 0 16px

    .image
      position: relative
      padding-top: 100%

      border-radius: 100%

      background-size: cover
      background-repeat: no-repeat
      background-position: center

      &::after
        position: absolute
        top: 0
        right: 0
        bottom: 0
        left: 0
        z-index: 2

        border: .5px solid rgba(0, 0, 0, .1)
        border-radius: 100%

        content: ''

    .lock,
    .unlock
      position: absolute
      top: auto
      right: 16px
      bottom: 0
      left: auto
      width: 20px
      height: 20px
      z-index: 120
      display: flex
      justify-conetnet: center
      align-items: center

      border-radius: 100%

      text-align: center

      .material-icons
        font-size: 0.75rem
        flex-grow: 1

    .lock
      color: white

    .unlock
      background-color: white

  .item-name
    margin-top: 16px
    flex-grow: 1

    font-weight: bold

    display: -webkit-box
    -webkit-line-clamp: 3
    -webkit-box-orient: vertical
    overflow: hidden

  .item-info
    margin-top: 2px

    font-size: 0.75rem
    font-weight: normal
    color: #8E8E8E

  .item-tool
    a,
    span
      margin: 16px auto 0
      padding: 8px 16px
      display: inline-block

      border-radius: 10px

      background: white

      text-align: center
      font-size: 0.75rem
      color: #8E8E8E
      font-weight: bold

    a
      cursor: pointer

    span
      padding: 8px 0

      background-color: transparent

.section--history
  text-align: center

.history
  .item
    position: relative
    padding: 16px

    text-align: left

    &:not(:last-child)
      &::after
        position: absolute
        top: auto
        right: 24px
        bottom: 0
        left: 24px
        height: 1px

        background-color: #E6E6E6

        content: ''

  .history-row
    display: flex
    align-items: center

    &.has-more
      cursor: pointer

  .left
    flex-grow: 1

  .right
    margin-left: 8px

  .detail-toggle
    position: relative
    width: 24px
    height: 24px

    color: rgba(51, 51, 51, .5)

    &.is-open
      transform: rotate(180deg)

    .toggle-loader
      position: absolute
      top: 0
      right: 0
      bottom: 0
      left: 0

      background-color: inherit

      span
        display: inline-block
        width: 4px
        height: 4px
        border-radius: 100%
        background-color: rgba(51, 51, 51, .5)
        margin: 10px 2px
        opacity: 0

        &:nth-child(1)
          animation: opacitychange .5s ease-in-out infinite

        &:nth-child(2)
          animation: opacitychange .5s ease-in-out .166666667s infinite

        &:nth-child(3)
          animation: opacitychange .5s ease-in-out .333333334s infinite

        @keyframes opacitychange
          0%, 100%
            opacity: 0

          60%
            opacity: 1

  .details
    margin-top: 16px
    padding: 8px 16px
    font-size: 0.875rem
    position: relative

    &::after
      position: absolute
      top: 0
      bottom: 0
      left: 0
      width: 3px
      opacity: .1

      border-radius: 0 6px 6px 0

      background-color: #333

      content: ''

  .detail
    display: flex

    &:not(:first-child)
      margin-top: 4px

    .name
      font-weight: bold
      margin-right: 16px
      flex-shrink: 0

    .value
      flex-grow: 1
      white-space: break-spaces
      word-break: break-all

  .more
    margin: 8px auto 0
    padding: 8px 16px
    display: inline-block

    border-radius: 10px

    background: #f5f5f5

    text-align: center
    font-size: 0.75rem
    font-weight: bold
    color: #8E8E8E

    cursor: pointer

  .content
    font-size: 0.875rem

  .time
    margin-top: 6px

  .time,
  .expire
    font-size: 0.75rem
    color: #8E8E8E

@keyframes bounceIn
  from,
  20%,
  40%,
  60%,
  80%,
  to
    animation-timing-function: cubic-bezier(0.215, 0.61, 0.355, 1)

  0%
    opacity: 0
    transform: scale3d(0.3, 0.3, 0.3)

  20%
    transform: scale3d(1.1, 1.1, 1.1)

  40%
    transform: scale3d(0.9, 0.9, 0.9)

  60%
    opacity: 1
    transform: scale3d(1.03, 1.03, 1.03)

  80%
    transform: scale3d(0.97, 0.97, 0.97)

  to
    opacity: 1
    transform: scale3d(1, 1, 1)

.fade-enter-active,
.fade-leave-active
  transition: opacity .25s $cb

.fade-enter,
.fade-leave-to
  opacity: 0

.qrcode-reader
  position: fixed
  top: 0
  right: 0
  bottom: 0
  left: 0
  z-index: 1090
  overflow: hidden

  background: black

  .qrcode-box
    position: fixed
    top: 50%
    right: 0
    bottom: 0
    left: 50%
    width: 50vw
    max-width: 400px
    z-index: 2

    transform: translate(-50%, -50%)

    animation: scale-up-center .75s cubic-bezier(0.390, 0.575, 0.565, 1.000) infinite alternate both;

  .qrcode-reader-close
    position: fixed
    top: auto
    right: auto
    bottom: 5%
    left: 50%
    z-index: 2
    padding: 8px 16px

    border: 0
    border-radius: 10px

    background-color: rgba(0, 0, 0, .25)

    font-weight: bold
    color: white
    font-size: 0.75rem
    text-align: center

    cursor: pointer
    transform: translateX(-50%)

  .qrcode-stream-wrapper
    position: fixed
    top: 0
    right: 0
    bottom: 0
    left: 0

@keyframes scale-up-center
  0%
    transform: translate(-50%, -50%) scale(1)

  100%
    transform: translate(-50%, -50%) scale(1.15)
</style>
